/* File: api.jsx
 * Path: ananunadm/src/helpers/api.jsx
 */
import axios from "axios";
import { cookies, setCookies, shouldRefreshPrivateToken } from "./cookies";
import config from "@/config";
import NProgress from "nprogress";
import "nprogress/nprogress.css"; // Change this line
import { getClientPlatformLabel, getOrCreateDeviceId } from "./deviceFingerprint";
import { getTenantKey } from "./tenant";
import { FINANCE_WORKSPACE_HEADER, getSelectedFinanceWorkspace, isFinanceApiRequest } from "./financeWorkspace";

let sessionExpiredHandler = null;
let authFailureHandled = false;
let privateTokenRefreshPromise = null;

/** In dev, route XHR through the CRA proxy (same origin) unless REACT_APP_API_USE_PROXY=false. */
function resolveApiRequestBaseUrl() {
	const version = config.api.API_Version || "v3";
	const explicit = String(config.api.API_URL || "").trim().replace(/\/$/, "");
	const useDevProxy =
		process.env.NODE_ENV === "development" &&
		process.env.REACT_APP_API_USE_PROXY !== "false";

	if (useDevProxy) {
		return `/${version}/`;
	}
	if (!explicit) {
		return `/${version}/`;
	}
	return `${explicit}/${version}/`;
}

const API = axios.create({
	baseURL: resolveApiRequestBaseUrl(),
	maxContentLength: 1000000000,
	maxBodyLength: 10000000000,
	timeout: 100000
});

export function isAuthenticationFailure(error) {
	const status = error?.response?.status;
	const errorCode = error?.response?.data?.error;
	const errorMessage = String(
		error?.response?.data?.message || error?.response?.data?.error || error?.message || ""
	).toLowerCase();

	if (status === 401 || status === 423) return true;
	if (errorCode === "#1") return true;
	if (errorCode === "SESSION_INVALID") return true;
	if (errorCode === "SESSION_INVALIDATED") return true;
	if (errorCode === "SESSION_REVOKED") return true;
	if (errorCode === "ACCOUNT_LOCKED") return true;
	if (errorCode === "IP_BLOCKED") return true;

	return (
		errorMessage.includes("expired token") ||
		errorMessage.includes("invalid token") ||
		errorMessage.includes("invalid or expired") ||
		errorMessage.includes("unauthorized")
	);
}

function notifySessionExpired(error) {
	if (!isAuthenticationFailure(error) || authFailureHandled) {
		return;
	}

	authFailureHandled = true;

	if (typeof sessionExpiredHandler === "function") {
		sessionExpiredHandler(error);
	}
}

export function registerSessionExpiredHandler(handler) {
	sessionExpiredHandler = handler;
	authFailureHandled = false;
}

function isPublicApiRequest(request = {}) {
	const publicPrefix = `/${config.api.API_Version}/public/`;
	const requestUrl = request.url || "";
	const baseUrl =
		request.baseURL ||
		(typeof window !== "undefined" ? window.location.origin : config.api.API_URL);

	try {
		const pathname = new URL(requestUrl, baseUrl).pathname;
		return pathname.startsWith(publicPrefix);
	} catch (_) {
		return String(requestUrl).startsWith("public/") || String(requestUrl).startsWith("/public/");
	}
}

function removePrivateTokenHeader(headers = {}) {
	if (typeof headers.delete === "function") {
		headers.delete("private-token");
		headers.delete("Private-Token");
		return;
	}

	delete headers["private-token"];
	delete headers["Private-Token"];
}

export async function refreshPrivateTokenIfNeeded(requestConfig = {}) {
	if (requestConfig.skipSessionRefresh || isPublicApiRequest(requestConfig)) return;

	const currentToken = cookies.get(config.api.tokenAddress);
	if (!shouldRefreshPrivateToken(currentToken)) return;

	if (!privateTokenRefreshPromise) {
		privateTokenRefreshPromise = API.post(
			"login/private/refresh",
			{ token: currentToken },
			{
				headers: { "private-token": currentToken },
				skipSessionRefresh: true
			}
		)
			.then((response) => {
				if (response?.data?.token) {
					setCookies(response.data.token);
				}
			})
			.finally(() => {
				privateTokenRefreshPromise = null;
			});
	}

	await privateTokenRefreshPromise;
}

API.interceptors.response.use(
	async (response) => {
		await refreshPrivateTokenIfNeeded(response?.config);
		return response;
	},
	(error) => {
		if (!error?.config?.skipAuthFailureHandler && !isPublicApiRequest(error?.config)) {
			notifySessionExpired(error);
		}
		return Promise.reject(error);
	}
);

API.interceptors.request.use((request) => {
	const token = cookies.get(config.api.tokenAddress);
	const deviceId = getOrCreateDeviceId();
	const timezone =
		typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "";

	request.headers = request.headers || {};
	if (isPublicApiRequest(request)) {
		removePrivateTokenHeader(request.headers);
		request.skipAuthFailureHandler = true;
	} else if (token && !request.headers["private-token"]) {
		request.headers["private-token"] = token;
	}
	request.headers["x-device-id"] = deviceId;
	if (timezone) {
		request.headers["x-client-timezone"] = timezone;
	}
	const clientPlatform = getClientPlatformLabel();
	if (clientPlatform) {
		request.headers["x-client-platform"] = clientPlatform;
	}
	const tenantKey = getTenantKey();
	if (tenantKey) {
		request.headers["x-apit-tenant"] = tenantKey;
	}
	const financeWorkspace = getSelectedFinanceWorkspace();
	if (financeWorkspace && isFinanceApiRequest(request.url) && !request.headers[FINANCE_WORKSPACE_HEADER]) {
		request.headers[FINANCE_WORKSPACE_HEADER] = financeWorkspace;
	}

	return request;
});

const settings = {
	headers: {
		"private-token": cookies.get(config.api.tokenAddress)
	}
};

// Keep the default provider upload under the configured 500 MB limit while avoiding
// hundreds of rapid POSTs that can trip Apache mod_evasive (DOSPageCount).
const DEFAULT_UPLOAD_CHUNK_SIZE = 8 * 1024 * 1024;
const SINGLE_CHUNK_THRESHOLD = 100 * 1024;

function emitUploadProgress(setProgress = () => {}, fileName = "", value = 0) {
	const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
	setProgress([{ name: fileName, value: safeValue }]);
}

export function isUploadCancelled(error) {
	if (!error) return false;
	if (error.code === "UPLOAD_CANCELLED") return true;
	if (error.name === "CanceledError" || error.code === "ERR_CANCELED") return true;
	return typeof axios.isCancel === "function" && axios.isCancel(error);
}

function createUploadCancelledError() {
	const error = new Error("Upload cancelled");
	error.code = "UPLOAD_CANCELLED";
	return error;
}

function getUploadFileParts(fileName = "") {
	const normalizedFileName = String(fileName || "").trim() || `upload_${Date.now()}`;
	const lastDotIndex = normalizedFileName.lastIndexOf(".");
	const hasExtension = lastDotIndex > 0 && lastDotIndex < normalizedFileName.length - 1;

	return {
		name: (hasExtension ? normalizedFileName.slice(0, lastDotIndex) : normalizedFileName)
			.replace(/\s+/g, "_")
			.trim(),
		extension: hasExtension ? normalizedFileName.slice(lastDotIndex) : ""
	};
}

function normalizeChunkPart(chunk, mimeType) {
	if (!chunk) return null;
	if (chunk instanceof Blob) return chunk;
	if (chunk instanceof ArrayBuffer) return new Blob([chunk], { type: mimeType });
	if (ArrayBuffer.isView(chunk)) return new Blob([chunk], { type: mimeType });
	return null;
}

function getSourceChunkParts(chunks, mimeType) {
	if (!Array.isArray(chunks) || chunks.length === 0) {
		return [];
	}

	return chunks
		.map((chunk) => normalizeChunkPart(chunk, mimeType))
		.filter((chunk) => chunk && chunk.size > 0);
}

function resolveUploadTotalSize(file, sourceParts, options = {}) {
	const fileSize = Number(file?.size) > 0 ? Number(file.size) : 0;
	const partsSize = sourceParts.reduce((sum, part) => sum + part.size, 0);
	const declaredSize = Number(options?.size) > 0 ? Number(options.size) : 0;
	return Math.max(fileSize, partsSize, declaredSize);
}

function resolveUploadChunks(file, options = {}) {
	const mimeType = options?.type || file?.type || "application/octet-stream";
	const splitChunkSize =
		Number(options?.chunkSize) > 0 ? Number(options.chunkSize) : DEFAULT_UPLOAD_CHUNK_SIZE;
	const sourceParts = getSourceChunkParts(options?.chunks, mimeType);
	const fileSize = Number(file?.size) > 0 ? Number(file.size) : 0;
	const totalSize = resolveUploadTotalSize(file, sourceParts, options);

	if (totalSize === 0) {
		return [];
	}

	if (totalSize < SINGLE_CHUNK_THRESHOLD) {
		if (sourceParts.length > 0) {
			return [new Blob(sourceParts, { type: mimeType })];
		}
		if (fileSize > 0 && typeof file.slice === "function") {
			return [file.slice(0, fileSize, mimeType)];
		}
		return [];
	}

	if (sourceParts.length > 0) {
		const mergedChunks = mergeChunksBySize(sourceParts, splitChunkSize, mimeType).filter(
			(chunk) => chunk instanceof Blob && chunk.size > 0
		);
		if (mergedChunks.length > 0) {
			return mergedChunks;
		}
	}

	if (fileSize > 0 && typeof file.slice === "function") {
		return buildFileChunks(file, splitChunkSize);
	}

	return sourceParts.length > 0 ? [new Blob(sourceParts, { type: mimeType })] : [];
}

function buildFileChunks(file, chunkSize) {
	const chunks = [];
	const fileSize = Number(file?.size) > 0 ? Number(file.size) : 0;

	for (let start = 0; start < fileSize; start += chunkSize) {
		chunks.push(file.slice(start, start + chunkSize, file.type));
	}

	return chunks;
}

function mergeChunksBySize(chunks = [], chunkSize, mimeType) {
	if (!Array.isArray(chunks) || chunks.length === 0) {
		return [];
	}

	const mergedChunks = [];
	let bufferedParts = [];
	let bufferedSize = 0;

	chunks.forEach((chunk) => {
		if (!(chunk instanceof Blob) || chunk.size === 0) {
			return;
		}

		if (bufferedSize > 0 && bufferedSize + chunk.size > chunkSize) {
			mergedChunks.push(new Blob(bufferedParts, { type: mimeType }));
			bufferedParts = [];
			bufferedSize = 0;
		}

		bufferedParts.push(chunk);
		bufferedSize += chunk.size;

		if (bufferedSize >= chunkSize) {
			mergedChunks.push(new Blob(bufferedParts, { type: mimeType }));
			bufferedParts = [];
			bufferedSize = 0;
		}
	});

	if (bufferedParts.length > 0) {
		mergedChunks.push(new Blob(bufferedParts, { type: mimeType }));
	}

	return mergedChunks;
}

export function get({
	route = "",
	query = {},
	sort = "",
	uploadType = "",
	limit = "",
	populate = "",
	skip = null,
	count = false,
	fields = "",
	signal,
	timeout
}) {
	settings.headers["Content-Type"] = "application/json";
	settings.headers["private-token"] = cookies.get(config.api.tokenAddress);
	return new Promise((resolve, reject) => {
		let Query = { ...query };
		if (Object.values(Query).includes("temporary")) {
		} else {
			Query.temporary = { $ne: true };
		}
		let completeRoute = `${route}${Query ? `?q=${JSON.stringify(Query)}` : ""}${
			sort ? `&sort=${JSON.stringify(sort)}` : ""
		}${uploadType ? `&uploadType=${JSON.stringify(uploadType)}` : ""}${
			skip ? `&skip=${JSON.stringify(skip)}` : ""
		}${limit ? `&limit=${JSON.stringify(limit)}` : ""}${
			populate ? `&populate=${JSON.stringify(populate)}` : ""
		}${count ? `&count=${JSON.stringify(count)}` : ""}${fields ? `&fields=${encodeURIComponent(fields)}` : ""}`;
		if (route && completeRoute && completeRoute.length !== 0) {
			API.get(completeRoute, {
				...settings,
				...(signal ? { signal } : {}),
				...(timeout != null ? { timeout } : {})
			})
				.then(({ data }) => {
					NProgress.done();
					resolve(data);
				})
				.catch((error) => reject(error));
		}
	});
}
export function post(route, data, axiosOptions = {}) {
	// Ensure `data` is a valid object
	if (!data || typeof data !== "object") {
		throw new TypeError("Data must be a non-null object.");
	}
	const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
	if (!isFormData) {
		// Clean data from null, undefined, and empty string values
		data = Object.fromEntries(
			Object.entries(data).filter(([, value]) => value !== undefined && value !== "")
		);
	}
	const headers = {
		...settings.headers,
		"private-token": cookies.get(config.api.tokenAddress),
		...(axiosOptions.headers || {})
	};
	if (!isFormData) {
		headers["Content-Type"] = "application/json";
	} else {
		// `settings.headers` is a shared, mutable object that get()/update()/patch()/remove()
		// permanently stamp with "Content-Type": "application/json" the moment any of them is
		// called (see below) — nothing ever resets it. Spreading `...settings.headers` above
		// can therefore hand a FormData upload a stale JSON content-type, which stops the
		// browser from generating the multipart boundary and leaves the server with no file
		// at all. FormData must always compute its own Content-Type (with boundary).
		delete headers["Content-Type"];
	}
	const merged = {
		...settings,
		...axiosOptions,
		headers
	};
	return new Promise((resolve, reject) => {
		API.post(route, data, merged)
			.then(({ data }) => resolve(data))
			.catch((error) => reject(error));
	});
}
export function put(route, data, axiosOptions = {}) {
	if (!data || typeof data !== "object") {
		throw new TypeError("Data must be a non-null object.");
	}

	data = Object.fromEntries(
		Object.entries(data).filter(([, value]) => value !== undefined && value !== "")
	);

	const headers = {
		...settings.headers,
		"private-token": cookies.get(config.api.tokenAddress),
		"Content-Type": "application/json",
		...(axiosOptions.headers || {})
	};
	const merged = {
		...settings,
		...axiosOptions,
		headers
	};

	return new Promise((resolve, reject) => {
		API.put(route, data, merged)
			.then(({ data }) => resolve(data))
			.catch((error) => reject(error));
	});
}
export function update(route, id, data, populate) {
	// Ensure `data` is a valid object
	if (!data || typeof data !== "object") {
		throw new TypeError("Data must be a non-null object.");
	}
	// Clean data from null, undefined, and empty string values
	data = Object.fromEntries(
		Object.entries(data).filter(([, value]) => value !== undefined && value !== "")
	);
	settings.headers["Content-Type"] = "application/json";
	settings.headers["private-token"] = cookies.get(config.api.tokenAddress);
	return new Promise((resolve, reject) => {
		const updateRoute = `${route}/${id}${populate ? `?populate=${JSON.stringify(populate)}` : ""}`;
		API.put(updateRoute, data, settings)
			.then(({ data }) => resolve(data))
			.catch((error) => {
				const status = error?.response?.status;
				if (status !== 404 && status !== 405) {
					reject(error);
					return;
				}

				API.patch(updateRoute, data, settings)
					.then(({ data }) => resolve(data))
					.catch((fallbackError) => reject(fallbackError));
			});
	});
}
export function patch(route, data) {
	if (!data || typeof data !== "object") {
		throw new TypeError("Data must be a non-null object.");
	}

	data = Object.fromEntries(
		Object.entries(data).filter(([, value]) => value !== undefined && value !== "")
	);

	settings.headers["Content-Type"] = "application/json";
	settings.headers["private-token"] = cookies.get(config.api.tokenAddress);

	return new Promise((resolve, reject) => {
		API.patch(route, data, settings)
			.then(({ data }) => resolve(data))
			.catch((error) => reject(error));
	});
}
export function remove(route, id) {
	settings.headers["Content-Type"] = "application/json";
	settings.headers["private-token"] = cookies.get(config.api.tokenAddress);
	return new Promise((resolve, reject) => {
		API.delete(`${route}/${id}`, settings)
			.then(({ data }) => resolve(data))
			.catch((error) => reject(error));
	});
}
/** DELETE `${route}/${id}/${subpath}/${subId}` — for nested sub-resources (e.g. task attachments) that `remove()`'s flat `${route}/${id}` shape can't address. */
export function removeNested(route, id, subpath, subId) {
	settings.headers["Content-Type"] = "application/json";
	settings.headers["private-token"] = cookies.get(config.api.tokenAddress);
	return new Promise((resolve, reject) => {
		API.delete(`${route}/${id}/${subpath}/${subId}`, settings)
			.then(({ data }) => resolve(data))
			.catch((error) => reject(error));
	});
}
async function retryUploadChunk(send, signal) {
    for (let attempt = 0; ; attempt++) {
        if (signal?.aborted) throw createUploadCancelledError();
        try { return await send(); }
        catch (error) {
            const status = error?.response?.status;
            if (signal?.aborted || isUploadCancelled(error) || attempt >= 5 ||
                (status && status !== 408 && status !== 429 && status < 500)) throw error;
            await new Promise(resolve => setTimeout(resolve, Math.min(15000, 1000 * 2 ** attempt)));
        }
    }
}

export const upload = (
	id,
	file,
	lang,
	destiny,
	thumbSizeWidth,
	thumbSizeHeight,
	responsiveImages,
	alt,
	setProgress = () => {},
	progress,
	date = new Date(),
	uploadedFiles,
	setUploadedFiles = () => {},
	options = {}
) => {
	return new Promise(async (resolve, reject) => {
		if (!file) {
			reject(new Error("No file provided"));
			return;
		}

		if (!(file instanceof File) && !(file instanceof Blob)) {
			console.warn("File parameter is not a File or Blob object. Skipping upload for:", file);
			resolve(file);
			return;
		}

		const originalFileName = options?.fileName || file?.name || `upload_${Date.now()}`;
		const { name, extension } = getUploadFileParts(originalFileName);
		const mimeType = options?.type || file?.type || "application/octet-stream";
		const chunks = resolveUploadChunks(file, options);

		if (chunks.length === 0) {
			reject(new Error("Cannot upload an empty file"));
			return;
		}

		const fileDate = options?.date || file?.date || date;
		const fileSize =
			Number(options?.size) > 0
				? Number(options.size)
				: chunks.reduce((sum, chunk) => sum + (chunk?.size || 0), 0);
		const totalChunks = chunks.length;
		// One id per file upload, sent on every chunk of it, so the server can
		// stage this upload's chunks under a name unique to this attempt instead
		// of the shared `name+extension` — see routes/upload/index.js. Without
		// this, two uploads that resolve to the same filename (concurrent or
		// not) can interleave or silently overwrite each other on disk.
		const uploadSessionId = createUploadSessionId();
		const baseHeaders = {
			"private-token": cookies.get(config.api.tokenAddress),
			"x-total-chunks": totalChunks,
			"x-file-size": fileSize,
			"x-upload-session": uploadSessionId
		};
		let uploadedBytes = 0;
		let information = null;

		void progress;

		emitUploadProgress(setProgress, originalFileName, 0);

		for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
			if (options?.signal?.aborted) {
				reject(createUploadCancelledError());
				return;
			}

			const chunk = chunks[chunkNumber];
			const uploadedBytesBeforeChunk = uploadedBytes;
			const formData = new FormData();
			formData.append("obj_id", id);
			formData.append("lang", lang);
			formData.append("destiny", destiny);
			formData.append("type", mimeType);
			formData.append("date", fileDate);
			formData.append("alt", alt);
			formData.append("name", name);
			formData.append("extension", extension);
			formData.append("thumbSizeWidth", thumbSizeWidth);
			formData.append("thumbSizeHeight", thumbSizeHeight);
			formData.append("responsiveImages", responsiveImages);
			formData.append("chunks", chunk, `${name || "chunk"}-${chunkNumber}${extension || ""}`);

			try {
				const { data } = await retryUploadChunk(() => API.post(config.api.system.upload, formData, {
					headers: {
						...baseHeaders,
						"x-current-chunk": chunkNumber,
                        "x-chunk-offset": uploadedBytesBeforeChunk
					},
					signal: options?.signal,
					timeout: 0,
					onUploadProgress: (uploadEvent) => {
						const loadedThisChunk = Math.min(chunk.size, uploadEvent?.loaded || 0);
						const totalLoaded = uploadedBytesBeforeChunk + loadedThisChunk;
						emitUploadProgress(
							setProgress,
							originalFileName,
							fileSize > 0 ? (totalLoaded / fileSize) * 100 : 0
						);
					}
				}), options?.signal);

				information = data?.upload ?? data;
				uploadedBytes += chunk.size;
				emitUploadProgress(
					setProgress,
					originalFileName,
					fileSize > 0 ? (uploadedBytes / fileSize) * 100 : 100
				);
			} catch (error) {
				if (options?.signal?.aborted || isUploadCancelled(error)) {
					reject(createUploadCancelledError());
					return;
				}
				reject(error);
				return;
			}
		}

		if (Array.isArray(uploadedFiles)) {
			const nextUploadedFiles = [...uploadedFiles, { name: originalFileName, value: 100 }];
			setUploadedFiles(nextUploadedFiles);
		}

		resolve(information);
	});
};

function createUploadSessionId() {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	return `upload_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export async function uploadFilesBrowserFile({
	route,
	file,
	fields = {},
	setProgress = () => {},
	signal,
	chunkSize = DEFAULT_UPLOAD_CHUNK_SIZE
}) {
	if (!file || !(file instanceof File) || !Number(file.size)) {
		throw new Error("No file provided");
	}

	const chunks = resolveUploadChunks(file, { chunkSize });
	const uploadSessionId = createUploadSessionId();
	const fileName = file.name;
	const fileSize = file.size;
	let uploadedBytes = 0;
	let lastResponse = null;

	emitUploadProgress(setProgress, fileName, 0);

	for (let chunkNumber = 0; chunkNumber < chunks.length; chunkNumber++) {
		if (signal?.aborted) {
			throw createUploadCancelledError();
		}

		const chunk = chunks[chunkNumber];
		const uploadedBytesBeforeChunk = uploadedBytes;
		const formData = new FormData();

		Object.entries(fields).forEach(([key, value]) => {
			if (value !== undefined && value !== null && value !== "") {
				formData.append(key, value);
			}
		});
		formData.append("fileName", fileName);
		formData.append("uploadSessionId", uploadSessionId);
		formData.append("chunks", chunk, `${fileName}-chunk-${chunkNumber}`);

		const { data } = await API.post(route, formData, {
			headers: {
				"x-total-chunks": chunks.length,
				"x-current-chunk": chunkNumber,
				"x-file-size": fileSize,
				"x-upload-session": uploadSessionId
			},
			signal,
			timeout: 0,
			onUploadProgress: (uploadEvent) => {
				const loadedThisChunk = Math.min(chunk.size, uploadEvent?.loaded || 0);
				const totalLoaded = uploadedBytesBeforeChunk + loadedThisChunk;
				emitUploadProgress(
					setProgress,
					fileName,
					fileSize > 0 ? (totalLoaded / fileSize) * 100 : 0
				);
			}
		});

		uploadedBytes += chunk.size;
		emitUploadProgress(setProgress, fileName, fileSize > 0 ? (uploadedBytes / fileSize) * 100 : 100);
		lastResponse = data;

		if (!data?.pending) {
			return data;
		}
	}

	return lastResponse;
}

export default API;
